import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { fillIT2104Form } from '../../../../../services/IT2104PDFService';
import { fillNC4Form } from '../../../../../services/NC4PDFService';
import { fillIS54Form } from '../../../../../services/IS54PDFService';
import { getCurrentW4Config } from '../../../../../helpers/w4FormConfig';
import { toLocaleFormatSlash } from '../../../../../helpers/dateFormat';
import { safeSetTextField } from '../../../../../helpers/pdfFieldHelpers';
import { embedSignatureImage, drawSignatureImage } from '../../../../../helpers/embedSignatureImage';
import { fetchPdfBytes } from '../../../../../helpers/fetchPdfBytes';

export { buildAgreementPdf } from './buildAgreementPdf';
export { buildEmployeeChildcareTuitionPdf, buildLaptopContractPdf } from './buildCustomAgreementPdfs';
export type { EmployeeChildcareTuitionData, LaptopContractData } from './buildCustomAgreementPdfs';

const enableReadOnly = (pdfForm: any) => {
  try {
    const fields = pdfForm.getFields();
    for (const f of fields) {
      const type = f.constructor.name;
      const fieldName = f.getName();
      if (type === 'PDFTextField') pdfForm.getTextField(fieldName).enableReadOnly();
      if (type === 'PDFDropdown') pdfForm.getDropdown(fieldName).enableReadOnly();
      if (type === 'PDFCheckBox') pdfForm.getCheckBox(fieldName).enableReadOnly();
    }
  } catch {
    // ignore
  }
};

export interface W4FilledData {
  firstName?: string;
  lastName?: string;
  ssn?: string;
  address?: string;
  city?: string;
  married?: '1' | '2' | '3';
  amounts?: { i: number; v: number }[];
  employerName?: string;
  ein?: string;
  date?: string;
}

export const buildW4Pdf = async (
  formData: W4FilledData,
  signaturePng?: string,
  signatureDate?: string,
): Promise<Uint8Array> => {
  const versionConfig = getCurrentW4Config();
  const arrayBuffer = await fetchPdfBytes(versionConfig.pdfUrl);
  const pdfDoc = await PDFDocument.load(arrayBuffer);
  const pdfForm = pdfDoc.getForm();
  const pages = pdfDoc.getPages();
  const firstPage = pages[0];
  const timesRomanFont = await pdfDoc.embedFont(StandardFonts.TimesRoman);

  if (firstPage) {
    if (signaturePng) {
      const png = await embedSignatureImage(pdfDoc, signaturePng);
      drawSignatureImage(firstPage, png, versionConfig.coordinatesSign);
    }
    if (signatureDate) {
      firstPage.drawText(toLocaleFormatSlash(signatureDate), {
        x: versionConfig.coordinatesDate.x,
        y: versionConfig.coordinatesDate.y,
        size: versionConfig.coordinatesDate.size ?? 12,
        font: timesRomanFont,
        color: rgb(0, 0, 0),
      });
    }
  }

  if (versionConfig.useFormFields) {
    enableReadOnly(pdfForm);
    const fieldNames = pdfForm.getFields().map((f) => f.getName());
    if (fieldNames.length) {
      const idx = versionConfig.fieldIndices;
      const emp = idx?.employerName ?? 15;
      const dat = idx?.date ?? 16;
      const ei = idx?.ein ?? 17;
      const getAmountIdx = idx?.getAmountFormIndex ?? ((i: number) => i);
      const setText = (index: number, value?: string) => {
        if (!fieldNames[index]) return;
        safeSetTextField(pdfForm, fieldNames[index], value);
      };
      const setCheck = (index: number, shouldCheck: boolean) => {
        if (!fieldNames[index]) return;
        try {
          const field = pdfForm.getCheckBox(fieldNames[index]);
          field.enableReadOnly();
          if (shouldCheck) field.check();
        } catch {
          // ignore
        }
      };
      setText(0, formData.firstName);
      setText(1, formData.lastName);
      setText(2, formData.address);
      setText(3, formData.city);
      setText(4, formData.ssn);
      setText(emp, formData.employerName);
      setText(dat, formData.date ? toLocaleFormatSlash(formData.date) : undefined);
      setText(ei, formData.ein);
      setCheck(5, formData.married === '1');
      setCheck(6, formData.married === '2');
      setCheck(7, formData.married === '3');
      if (idx?.step3Total != null) {
        const a9 = formData.amounts?.find((a) => a.i === 9)?.v ?? 0;
        const a10 = formData.amounts?.find((a) => a.i === 10)?.v ?? 0;
        setText(idx.step3Total, String(Number(a9) + Number(a10)));
      }
      for (const n of formData.amounts || []) {
        if (n.i === undefined) continue;
        setText(getAmountIdx(n.i), String(n.v ?? 0));
      }
    }
  }

  return pdfDoc.save();
};

const I9_ENGLISH_PDF =
  'https://arb-systems-s3-public.s3.us-east-2.amazonaws.com/public/Employment-Eligibility/i-9-english.pdf';

const I9_SPANISH_PDF =
  'https://arb-systems-s3-public.s3.us-east-2.amazonaws.com/public/Employment-Eligibility/i-9-spanish.pdf';

export interface I9FormFieldValue {
  fieldType: 'PDFTextField' | 'PDFDropdown' | 'PDFCheckBox';
  fieldName: string;
  fieldValue?: string;
}

interface BuildI9Args {
  spanish?: boolean;
  fieldValues: I9FormFieldValue[];
  signaturePng?: string;
  supervisorSignaturePng?: string;
}

const I9_COORDS = {
  english: {
    employee: { x: 60, y: 420, height: 30, opacity: 1 },
    supervisor: { x: 300, y: 75, height: 30, opacity: 1 },
    supervisorPageIndex: 0,
  },
  spanish: {
    employee: { x: 60, y: 395, height: 30, opacity: 1 },
    supervisor: { x: 260, y: 355, height: 30, opacity: 1 },
    supervisorPageIndex: 1,
  },
};

export const buildI9Pdf = async ({
  spanish,
  fieldValues,
  signaturePng,
  supervisorSignaturePng,
}: BuildI9Args): Promise<Uint8Array> => {
  const url = spanish ? I9_SPANISH_PDF : I9_ENGLISH_PDF;
  const coords = spanish ? I9_COORDS.spanish : I9_COORDS.english;
  const arrayBuffer = await fetchPdfBytes(url);
  const pdfDoc = await PDFDocument.load(arrayBuffer);
  const pdfForm = pdfDoc.getForm();
  const pages = pdfDoc.getPages();
  const firstPage = pages[0];

  for (const f of fieldValues) {
    if (!f.fieldValue) continue;
    try {
      if (f.fieldType === 'PDFTextField') {
        safeSetTextField(pdfForm, f.fieldName, f.fieldValue);
      } else if (f.fieldType === 'PDFDropdown') {
        const field = pdfForm.getDropdown(f.fieldName);
        field.select(f.fieldValue);
        field.enableReadOnly();
      } else if (f.fieldType === 'PDFCheckBox' && f.fieldValue === 'true') {
        const field = pdfForm.getCheckBox(f.fieldName);
        field.check();
        field.enableReadOnly();
      }
    } catch {
      // ignore
    }
  }

  if (firstPage && signaturePng) {
    const png = await embedSignatureImage(pdfDoc, signaturePng);
    drawSignatureImage(firstPage, png, coords.employee);
  }

  if (supervisorSignaturePng) {
    const supervisorPage = pages[coords.supervisorPageIndex] || firstPage;
    if (supervisorPage) {
      const png = await embedSignatureImage(pdfDoc, supervisorSignaturePng);
      drawSignatureImage(supervisorPage, png, coords.supervisor);
    }
  }

  return pdfDoc.save();
};

export const buildIT2104Pdf = async (
  pdfUrl: string,
  formData: any,
  signaturePng?: string,
): Promise<Uint8Array | null> => {
  return fillIT2104Form(pdfUrl, { ...formData, employeeSignature: signaturePng || formData.employeeSignature });
};

export const buildNC4Pdf = async (pdfUrl: string, formData: any, signaturePng?: string): Promise<Uint8Array | null> => {
  return fillNC4Form(pdfUrl, { ...formData, employeeSignature: signaturePng || formData.employeeSignature });
};

export const buildIS54Pdf = async (
  pdfUrl: string,
  formData: any,
  signaturePng?: string,
): Promise<Uint8Array | null> => {
  return fillIS54Form(pdfUrl, { ...formData, employeeSignature: signaturePng || formData.employeeSignature });
};
